import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { HelixLoader } from '../../../../components';
import { useDictionaryList } from '../../../../hooks';
import { Dictionary } from '../../../../lib';
import {
    ChainMetric,
    ChainMetrics,
    chartStyles,
    dashboardContainerStyles,
    METRICS,
    MIN_BAR_WIDTH_FOR_SELECTED_CHAINS,
    MIN_BAR_WIDTH,
    normalizeChartData,
} from './dashboard-chart-helpers';
import { Box, Grid, Typography } from '@mui/material';
import { ChainSvg } from '../../../../assets/images';

interface DashboardChartProps {
    data: ChainMetrics;
    selectedMetric: string;
    selectedChains: string[];
    setIsManageChainsFilterVisible: React.Dispatch<
        React.SetStateAction<boolean>
    >;
    setFilteredChains: React.Dispatch<React.SetStateAction<Dictionary[]>>;
}
const DashboardChart = ({
    data,
    selectedMetric,
    selectedChains,
    setIsManageChainsFilterVisible,
    setFilteredChains,
}: DashboardChartProps): React.JSX.Element => {
    const { data: dictionaryList, isLoading: areDictionaryLoading } =
        useDictionaryList();
    const dictionaries: Dictionary[] = dictionaryList?.data ?? [];
    const [filteredBarData, setFilteredBarData] = useState<any>([]);

    const calculatedWidth = useMemo(() => {
        const dynamicWidth = filteredBarData.length * MIN_BAR_WIDTH;

        return Math.max(dynamicWidth, MIN_BAR_WIDTH_FOR_SELECTED_CHAINS);
    }, [filteredBarData.length]);

    useEffect(() => {
        if (!areDictionaryLoading) {
            const normalizedMetrics: ChainMetrics = normalizeChartData(
                data,
                dictionaries
            );
            // Filter out any chains whose selected metric value is zero.
            const filteredData = Object.values(normalizedMetrics).filter(
                (entry: ChainMetric) =>
                    entry[selectedMetric as keyof typeof entry] !== 0 &&
                    (selectedChains.length > 0
                        ? selectedChains.includes(
                              entry?.chain as keyof typeof entry
                          )
                        : true)
            );
            let filteredChainsData = [];
            if (filteredData.length === 0) {
                setFilteredBarData([]);
                setFilteredChains([]);
                setIsManageChainsFilterVisible(false);
            } else {
                setFilteredBarData(filteredData);
                filteredChainsData =
                    dictionaries.filter((chain) =>
                        filteredData.some(
                            (barData: any) => barData.chain === chain.name
                        )
                    ) || [];
                if (filteredChainsData.length) {
                    setIsManageChainsFilterVisible(true);
                }
                if (selectedChains.length === 0) {
                    setFilteredChains(filteredChainsData);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areDictionaryLoading, selectedChains, selectedMetric, data]);

    const getDatumColor = (datum: any): string => {
        return datum.color;
    };

    const getBarHoverLabel = (value: string): string => {
        const foundLabel = METRICS.find((metric) => value === metric.value);
        return foundLabel ? foundLabel.name : '';
    };

    if (areDictionaryLoading) {
        return <HelixLoader />;
    }

    if (filteredBarData.length === 0) {
        return (
            <Grid
                sx={dashboardContainerStyles.emptyStateContainer}
                data-testid='empty-state'
            >
                <Grid>
                    <img src={ChainSvg} alt='Empty_State_Image' />
                </Grid>
                <Grid mt={4}>
                    <Typography sx={dashboardContainerStyles.emptyHeadingText}>
                        Nothing to see here.
                    </Typography>
                </Grid>
                <Grid mt={2}>
                    <Typography
                        sx={dashboardContainerStyles.emptySubHeadingText}
                    >
                        There are no results that match the selected filter
                        criteria.
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    const leftAxisLegend: string = `TOTAL ${getBarHoverLabel(selectedMetric).toUpperCase()}`;

    // sticky Y-AXIS bar that displays the corresponding tick y-values.
    return (
        <Grid sx={dashboardContainerStyles.dashboardContainer} container>
            <Box sx={dashboardContainerStyles.dashboardYAxisContainer}>
                <ResponsiveBar
                    data-testid='y-axis-dashboard-chart'
                    data={filteredBarData}
                    theme={chartStyles.chartTheme}
                    keys={[selectedMetric]}
                    indexBy={'chain'}
                    margin={{ top: 50, right: 0, bottom: 120, left: 100 }}
                    padding={0.3}
                    valueScale={{ type: 'linear' }}
                    borderColor={{
                        from: 'color',
                        modifiers: [['darker', 1.6]],
                    }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={null}
                    axisLeft={{
                        tickValues: 10,
                        legend: `${leftAxisLegend}`,
                        legendPosition: 'middle',
                        legendOffset: -60,
                        renderTick: ({
                            x,
                            y,
                            textAnchor,
                            textX,
                            textY,
                            value,
                        }) => (
                            <g transform={`translate(${x},${y})`}>
                                <text
                                    textAnchor={textAnchor}
                                    transform={`translate(${textX},${textY + 3})`}
                                    style={{
                                        fontSize: 10,
                                        fontFamily: 'Red Hat Text',
                                    }}
                                >
                                    {value}
                                </text>
                            </g>
                        ),
                    }}
                    legends={[]}
                />
            </Box>
            {/* Dynamic X-Axis bar that displays the bar values and is horizontally scrollable if there are moe than 36 chains. */}
            <Box sx={dashboardContainerStyles.dashboardXAxisContainer}>
                <Box
                    sx={{
                        width:
                            filteredBarData.length > 36
                                ? `${calculatedWidth}px`
                                : 'calc(100% - 120px)',
                        height: '500px',
                    }}
                >
                    <ResponsiveBar
                        data-testid='x-axis-dashboard-chart'
                        data={filteredBarData}
                        theme={chartStyles.chartTheme}
                        keys={[selectedMetric]}
                        minValue={'auto'}
                        maxValue={'auto'}
                        indexBy={'chain'}
                        margin={{ top: 50, right: 20, bottom: 120, left: 100 }}
                        padding={0.3}
                        valueScale={{ type: 'linear' }}
                        indexScale={{ type: 'band', round: true }}
                        colors={(normalizedData): string =>
                            getDatumColor(normalizedData.data)
                        }
                        enableTotals={true}
                        enableLabel={false}
                        borderColor={{
                            from: 'color',
                            modifiers: [['darker', 1.6]],
                        }}
                        axisTop={null}
                        axisRight={null}
                        axisBottom={{
                            tickSize: 5,
                            tickPadding: 0,
                            legend: 'CHAIN',
                            tickRotation: 45,
                            legendPosition: 'middle',
                            legendOffset: 85,
                            format: (value): any => {
                                const truncatedLabel =
                                    value.length > 12
                                        ? `${value.slice(0, 12)}...`
                                        : value;
                                return truncatedLabel;
                            },
                        }}
                        axisLeft={null}
                        labelSkipWidth={12}
                        labelSkipHeight={12}
                        legends={[]}
                        role='application'
                        ariaLabel='BIQ Dashboard Chart'
                        barAriaLabel={(e: any): string => {
                            return (
                                e.id +
                                ': ' +
                                e.formattedValue +
                                ' in Chain: ' +
                                e.indexValue
                            );
                        }}
                        tooltipLabel={(e: any): string => {
                            return (
                                getBarHoverLabel(e.id) + ' - ' + e.indexValue
                            );
                        }}
                        enableGridY={true}
                    />
                </Box>
            </Box>
        </Grid>
    );
};

export default DashboardChart;
